import {jsx, ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {UserIconEl} from 'common/widgets/user-icon';
import {IconSizeType} from 'common/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {TopCreator} from 'common/report/queries/app-api/project-top-creators';
import {getCreatorNameLabel, getSuggestedCreators} from 'common/report/util/search';
import {pluralize} from 'common/util/string';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';

import css from './index.inline.styl';

type BrowseSuggestedCreatorsParams = {
  onCreatorClick?: (creator: TopCreator, position: number) => void;
  userId: number;
};

interface BrowseSuggestedCreatorsJsxProps {
  params: BrowseSuggestedCreatorsParams;
}

const CreatorCard = ({
  creator,
  onCreatorClick,
  position,
  userId,
}: {
  creator: TopCreator;
  onCreatorClick: (creator: TopCreator, position: number) => void;
  position: number;
  userId: number;
}) => {
  const boardsLabel =
    creator.dashboards_count > 0 ? `${creator.dashboards_count} ${pluralize(`board`, creator.dashboards_count)}` : ``;
  const reportsLabel =
    creator.bookmarks_count > 0 ? `${creator.bookmarks_count} ${pluralize(`report`, creator.bookmarks_count)}` : ``;
  const creationsLabel = [boardsLabel, reportsLabel].filter((label) => label).join(` • `);

  return (
    <ButtonEl
      sel='.browse-creator-button'
      attrs={{
        theme: ButtonTheme.Secondary,
        elref: `creatorCard`,
      }}
      on={{click: () => onCreatorClick(creator, position)}}
    >
      <div sel='.browse-creator-card'>
        <UserIconEl
          sel='.creator-card-icon'
          attrs={{
            id: creator.id,
            name: creator.name,
            email: creator.email,
            size: IconSizeType.Medium,
          }}
        />
        <div sel='.creator-card-name'>{getCreatorNameLabel(creator, userId)}</div>
        {/* `0 boards • 0 reports` case shouldn't happen, because we are not displaying users with 0 creations */}
        <div sel='.creator-entities-count'>{creationsLabel ? creationsLabel : `0 boards • 0 reports`}</div>
      </div>
    </ButtonEl>
  );
};

export class BrowseSuggestedCreators extends ParamComponent<
  BrowseSuggestedCreatorsParams,
  EmptyObj,
  IronContextRegistry
> {
  get config() {
    return {
      contexts: [`projectTopCreators` as const, `currentUser` as const],
      useShadowDom: true,
      css,
      template: () => {
        const creators = this.getSuggestedCreators();
        if (!creators.length) {
          return null;
        }

        return (
          <div sel='.browse-suggested-creators'>
            <div sel='.browse-suggested-creators-title'>Suggested Creators</div>

            <div sel='.browse-suggested-creators-list'>
              {creators.map((creator, idx) => {
                return (
                  <CreatorCard
                    creator={creator}
                    onCreatorClick={this.params.onCreatorClick}
                    userId={this.params.userId}
                    position={idx}
                  />
                );
              })}
            </div>
          </div>
        );
      },
      params: {
        onCreatorClick: Function,
        userId: Number,
      },
    };
  }

  getSuggestedCreators(): TopCreator[] {
    const topCreators = this.getContext(`projectTopCreators`).getCreators();
    return getSuggestedCreators(userCookieUtils.getUserIdentityFromCookie()?.id, topCreators);
  }
}

export const BrowseSuggestedCreatorsEl = defineAndMakeJsxFactory<BrowseSuggestedCreatorsJsxProps>(
  `browse-suggested-creators`,
  BrowseSuggestedCreators,
);
export const BrowseSuggestedCreatorsReact = panel2react<BrowseSuggestedCreatorsJsxProps>(
  `browse-suggested-creators`,
  BrowseSuggestedCreators,
);
